// Moved from `src/hooks/mutation/useMutateUpdateUserOnboardingTask.ts` (now the
// app delegate) with the identity D2 slice (ARCFE-1062). Unblocked by D1
// (ARCFE-1037), which carved `updateUserPendingOnboardingTasks` into
// `../api/user`.
//
// Severed per ARCFE-921: the destructive toast. `invalidateCurrentUser()`
// crosses as a port for the OT4 reason — it is COMPOSITE (GET_USER *and* the
// commerce GET_PRODUCTS behind one try/catch).
//
// The `GET_USER` optimistic filter stays here: it rewrites only the
// workspace-owned current-user cache.
import { logger } from '@heidi/runtime/logger';
import { type OnboardingTask } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_USER, type UserResponse } from '../../contracts';
import { updateUserPendingOnboardingTasks } from '../api/user';
import { useQueryUser } from '../query/useQueryUser';

const UPDATE_USER_ONBOARDING_TASK = 'useMutateUpdateUserOnboardingTask';

export interface UseMutateUpdateUserOnboardingTaskOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onOnboardingTaskUpdated?: () => void;
  /**
   * App-reaction port: fired where the legacy destructive toast fired, after
   * the log. Takes the raw `Error` because that toast's description is
   * `error.message`.
   */
  onOnboardingTaskError?: (error: Error) => void;
}

/** PATCH /user/onboarding-tasks/{task} */
export function useMutateUpdateUserOnboardingTask(
  options?: UseMutateUpdateUserOnboardingTaskOptions
) {
  const { data: userData } = useQueryUser();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [UPDATE_USER_ONBOARDING_TASK],
    mutationFn: async (task: OnboardingTask) => {
      if (task && userData) {
        await updateUserPendingOnboardingTasks(task);
      }
    },
    onSuccess: () => {
      options?.onOnboardingTaskUpdated?.();
    },
    onMutate: (task: OnboardingTask) => {
      void queryClient.setQueryData([GET_USER], (oldData: UserResponse) => {
        if (!oldData) {
          return undefined;
        }
        return {
          ...oldData,
          pending_onboarding_tasks: oldData.pending_onboarding_tasks.filter(
            (t) => t !== task
          ),
        };
      });
    },
    onError: (error) => {
      logger.error('Error:', error);
      options?.onOnboardingTaskError?.(error);
    },
  });
}
